import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Share, Plus, Check, X } from 'lucide-react';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { getColors, fonts } from '../theme';
import { useApp } from '../context/AppContext';

/**
 * PWA Install Prompt Component
 * 
 * Shows a minimalist bottom bar prompting users to install the app.
 * - On Android/Desktop: Shows "Install Now" button
 * - On iOS: Shows instructions for manual "Add to Home Screen"
 */
const InstallPWA = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const navigate = useNavigate();
  const [showIOSTooltip, setShowIOSTooltip] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  
  const {
    shouldShowInstallUI,
    isIOS,
    isInstalling,
    installApp,
    dismissPrompt,
  } = usePWAInstall();

  // Don't render if we shouldn't show install UI
  if (!shouldShowInstallUI) {
    return null;
  }

  const handleInstall = async () => {
    const result = await installApp();
    if (result.success) {
      // Install was accepted - UI will auto-hide via state
    }
  };

  const handleDismiss = () => {
    // Show checked state for visual confirmation
    setIsDismissing(true);
    // Delay actual dismissal so user sees the checkmark
    setTimeout(() => {
      dismissPrompt();
      setShowIOSTooltip(false);
    }, 600);
  };

  // iOS-specific instructions tooltip
  if (isIOS) {
    return (
      <>
        {/* Backdrop for iOS tooltip */}
        {showIOSTooltip && (
          <div 
            className="fixed inset-0 z-[998]"
            style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
            onClick={() => setShowIOSTooltip(false)}
          />
        )}
        
        {/* iOS Instructions Modal */}
        {showIOSTooltip && (
          <div 
            className="fixed bottom-24 left-4 right-4 z-[999] p-5 rounded-lg max-w-sm mx-auto"
            style={{
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`,
            }}
          >
            <button
              onClick={() => setShowIOSTooltip(false)}
              className="absolute top-3 right-3 p-1.5 rounded-full transition-all hover:opacity-70"
              style={{ backgroundColor: colors.bgTertiary }}
            >
              <X size={14} style={{ color: colors.textMuted }} />
            </button>
            
            <h3 
              className="text-base font-semibold mb-3 tracking-tight"
              style={{ color: colors.textPrimary }}
            >
              Add to Home Screen
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div 
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: colors.info + '20' }}
                >
                  <Share size={14} style={{ color: colors.info }} />
                </div>
                <p className="text-sm" style={{ color: colors.textSecondary }}>
                  Tap the <strong style={{ color: colors.textPrimary }}>Share</strong> button in Safari's toolbar
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div 
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: colors.accent + '20' }}
                >
                  <Plus size={14} style={{ color: colors.accent }} />
                </div>
                <p className="text-sm" style={{ color: colors.textSecondary }}>
                  Scroll down and tap <strong style={{ color: colors.textPrimary }}>"Add to Home Screen"</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* iOS Bottom Bar */}
        <div 
          className="fixed bottom-0 left-0 right-0 z-[997] px-3 py-3 safe-area-pb"
          style={{
            backgroundColor: `${colors.bgSecondary}f5`,
            borderTop: `1px solid ${colors.border}`,
            
            
          }}
        >
          <div className="max-w-lg mx-auto">
            {/* Top row: Message + How to Add button */}
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <p 
                  className="text-xs sm:text-sm"
                  style={{ color: colors.textPrimary }}
                >
                  Add <span style={{ fontFamily: fonts.serif, fontWeight: 600 }}>mind.</span> to home screen
                </p>
                <button
                  onClick={() => navigate('/pwa-info')}
                  className="text-xs mt-0.5 underline underline-offset-2 transition-opacity hover:opacity-70"
                  style={{ color: colors.textMuted }}
                >
                  Learn more
                </button>
              </div>
              
              <button
                onClick={() => setShowIOSTooltip(true)}
                className="px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all hover:opacity-90 active:scale-95 flex-shrink-0"
                style={{
                  backgroundColor: colors.accent,
                  color: colors.onAccent,
                }}
              >
                How to Add
              </button>
            </div>
            
            {/* Bottom row: Dismiss checkbox */}
            <button
              onClick={handleDismiss}
              disabled={isDismissing}
              className="flex items-center gap-2 w-full group"
            >
              <div 
                className="w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all"
                style={{ 
                  borderColor: isDismissing ? colors.accent : colors.textMuted,
                  backgroundColor: isDismissing ? colors.accent : 'transparent',
                }}
              >
                {isDismissing && <Check size={12} style={{ color: colors.bg }} />}
              </div>
              <span 
                className="text-xs transition-all"
                style={{ color: isDismissing ? colors.textPrimary : colors.textMuted, opacity: isDismissing ? 1 : 0.8 }}
              >
                {isDismissing ? 'Got it! Hiding this prompt...' : "I've added this to my home screen. Don't show again."}
              </span>
            </button>
          </div>
        </div>
      </>
    );
  }

  // Android/Desktop Install Bar
  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[997] px-3 py-3 safe-area-pb animate-slide-up"
      style={{
        backgroundColor: `${colors.bgSecondary}f5`,
        borderTop: `1px solid ${colors.border}`,
        
        
      }}
    >
      <div className="max-w-lg mx-auto">
        {/* Top row: Message + Install button */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <p 
              className="text-xs sm:text-sm"
              style={{ color: colors.textPrimary }}
            >
              Add <span style={{ fontFamily: fonts.serif, fontWeight: 600 }}>mind.</span> to home screen
            </p>
            <button
              onClick={() => navigate('/pwa-info')}
              className="text-xs mt-0.5 underline underline-offset-2 transition-opacity hover:opacity-70"
              style={{ color: colors.textMuted }}
            >
              Learn more
            </button>
          </div>
          
          <button
            onClick={handleInstall}
            disabled={isInstalling}
            className="px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium flex items-center gap-1.5 transition-all hover:opacity-90 active:scale-95 disabled:opacity-60 flex-shrink-0"
            style={{
              backgroundColor: colors.accent,
              color: colors.onAccent,
            }}
          >
            {isInstalling ? (
              <>
                <div 
                  className="w-3.5 h-3.5 border-2 rounded-full animate-spin"
                  style={{ borderColor: `${colors.bg}40`, borderTopColor: colors.bg }}
                />
                <span className="hidden sm:inline">Installing...</span>
              </>
            ) : (
              <>
                <Download size={14} />
                Install
              </>
            )}
          </button>
        </div>
        
        {/* Bottom row: Dismiss checkbox */}
        <button
          onClick={handleDismiss}
          disabled={isDismissing}
          className="flex items-center gap-2 w-full group"
        >
          <div 
            className="w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all"
            style={{ 
              borderColor: isDismissing ? colors.accent : colors.textMuted,
              backgroundColor: isDismissing ? colors.accent : 'transparent',
            }}
          >
            {isDismissing && <Check size={12} style={{ color: colors.bg }} />}
          </div>
          <span 
            className="text-xs transition-all"
            style={{ color: isDismissing ? colors.textPrimary : colors.textMuted, opacity: isDismissing ? 1 : 0.8 }}
          >
            {isDismissing ? 'Got it! Hiding this prompt...' : "I've added this to my home screen. Don't show again."}
          </span>
        </button>
      </div>
    </div>
  );
};

export default InstallPWA;
